import styled from '@emotion/styled';
import React, { FC } from 'react';
import { PageButtonType, PageType } from '..';
import Icon from '~ComponentsImproved/Icon';
import Payments from '~Assets/icons/payments.svg';
import TicketIcon from '~Assets/icons/receipt_long.svg';
import Receipt from '~Assets/icons/receipt.svg';
import Print from '~Assets/icons/print.svg';
import KDS from '~Assets/icons/product_kitchen-display.svg';
import Layout from '~Assets/icons/edit_layout.svg';
import Other from '~Assets/icons/settings.svg';
import Station from '~Assets/icons/product_table-management.svg';
import Zone from '~Assets/icons/boxes_stacked.svg';
import TablesIcon from '~Assets/icons/restaurant.svg';
import ReportIcon from '~Assets/icons/article.svg';
import StaffIcon from '~Assets/icons/person.svg';
import AppIcon from '~Assets/icons/phone_android.svg';
import Heading from '~ComponentsImproved/Typography/Heading';
import { useButton, useI18n } from '~Hooks';

const PAGE_COLORS: Record<PageButtonType, string> = {
  printers: '#1fb4ff',
  layout: '#f69ed3',
  payments: '#93df93',
  receipts: '#ffb86c',
  KDS: '#f2d8a9',
  'kitchen-tickets': '#ada9f2',
  zones: '#f2bfa9',
  tables: '#a9d4f2',
  station: '#f2a9a9',
  other: '#c2c2c2',
  safT: '#f2a9f2',
  ejournal: '#f2a9f2',
  'staff-settings': '#fff',
  app: '#6bbfc3',
};

const SettingsButton = styled.div<{ page: PageButtonType; touching?: boolean }>`
  background-color: var(--colorBlueGray1100);
  border-radius: 16px;
  padding: 8px;
  box-sizing: border-box;
  height: 148px;
  width: 148px;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  text-align: center;
  transform: ${({ touching }) => (touching ? 'scale(0.96)' : '1')};
`;

const IconWrapper = styled.div`
  margin-bottom: 4px;
  flex-shrink: 0;
`;

const ICONS: Record<PageButtonType, string> = {
  printers: Print,
  layout: Layout,
  payments: Payments,
  receipts: Receipt,
  KDS: KDS,
  'kitchen-tickets': TicketIcon,
  zones: Zone,
  tables: TablesIcon,
  station: Station,
  other: Other,
  safT: ReportIcon,
  ejournal: ReportIcon,
  'staff-settings': StaffIcon,
  app: AppIcon,
};

interface Props {
  page: PageButtonType;
  setSelectedPage: (page: PageButtonType) => void;
}

const PageButton: FC<Props> = ({ page, setSelectedPage }) => {
  const { i18n } = useI18n();

  const button = useButton({
    onClick: () => setSelectedPage(page),
  });

  const BUTTON_TITLES: Record<PageType, string> = {
    printers: i18n('Settings.Printers'),
    layout: i18n('Settings.PosLayout'),
    payments: i18n('Settings.Payments'),
    receipts: i18n('Settings.Receipts'),
    KDS: i18n('Settings.KdsSettings'),
    'kitchen-tickets': i18n('Settings.KitchenTickets'),
    zones: i18n('Settings.Zone'),
    tables: i18n('Settings.Tables'),
    station: i18n('Settings.Station'),
    safT: 'Saf-T',
    ejournal: i18n('Settings.EJournal'),
    other: i18n('Settings.Other'),
    developer: 'Developer',
    'staff-settings': i18n('Settings.StaffSettings'),
    app: i18n('Settings.AppSettings'),
  };

  return (
    <SettingsButton key={page} page={page} {...button}>
      <IconWrapper>
        <Icon src={ICONS[page]} height="58px" width="48px" color={PAGE_COLORS[page]} />
      </IconWrapper>
      <Heading level={4} bottom={4} color="#fff">
        {BUTTON_TITLES[page]}
      </Heading>
    </SettingsButton>
  );
};

export default PageButton;
